'use client';

import { useParams, usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { MARKETING_CDN, careersHref, cultivPath, demoHref, loginHref, watchDemoHref } from '../site';

/* The marketing site's agency chat widget injector, mounted once from the (site) layout.
   Was a copy of the old Koqoon ChatWidgetEffect — identical config (same loader, agency001,
   agent id, MFA, text style, start-open) except the brand colour and the favicon shown in
   the widget chrome. The Koqoon original is gone: /france is a Cultiv page now and this is
   the only injector left. */
export default function CultivChatWidget() {
  const params = useParams();
  const pathname = usePathname();
  const locale = params.locale as string;
  // No widget on login, book-a-demo (/demo), product-tour or careers: auth and
  // application forms, where a chat bubble competes with what the visitor is
  // already filling in. Prefix matches, so nested pages are covered too.
  // Mounted from the persistent layout, so this must react to client-side
  // navigation, not just mount. Derived from the href helpers rather than
  // hardcoded '/cultiv/...' strings, which the root-route move would have
  // silently turned into permanent non-matches with nothing to catch it.
  const suppressed = [
    loginHref(locale),
    demoHref(locale),
    watchDemoHref(locale),
    careersHref(locale),
  ].some((path) => pathname?.startsWith(path));
  // The France-market landing (/[locale]/france) forces the widget's FR flow +
  // dictionary via data-country. Written for a /cultiv/france route that never
  // existed; live now that /france IS the Cultiv home.
  const country = pathname?.startsWith(cultivPath(locale, '/france')) ? 'FR' : undefined;

  useEffect(() => {
    // Remove existing iframe if it exists
    const existingIframe = document.getElementById('chatIframe');
    if (existingIframe) {
      existingIframe.remove();
    }

    if (suppressed) return;

    // Remove existing script if it exists
    const existingScript = document.getElementById('chatWidgetScript');
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const newScript = document.createElement('script');
    newScript.id = 'chatWidgetScript';

    const isLocal = window.location.hostname === 'localhost';
    newScript.src = isLocal
      ? '/widget/chat-widget-avm-loader-local.js'
      : 'https://d1oytcss5issfa.cloudfront.net/widget/chat-widget-avm-loader.js';

    newScript.setAttribute('data-agency-id', 'agency001');
    newScript.setAttribute('data-agent-id', 'user-1-6ba7b810-9dad-11d1-80b4-00c04fd430c8');
    newScript.setAttribute('data-background-color', '#E9551B'); // Cultiv Signal Orange
    newScript.setAttribute('data-favicon-url', `${MARKETING_CDN}/cultiv-symbol-orange.svg`);
    newScript.setAttribute('data-enable-mfa', 'true');
    newScript.setAttribute('data-text-style', '{"color":"#FFFFFF"}');
    newScript.setAttribute('data-default-locale', locale);
    newScript.setAttribute('data-start-open', 'false');
    if (country) {
      newScript.setAttribute('data-country', country);
    }

    // Add script to head
    document.head.appendChild(newScript);

    // Cleanup function
    return () => {
      if (newScript.parentNode) {
        newScript.parentNode.removeChild(newScript);
      }
      const iframe = document.getElementById('chatIframe');
      if (iframe) {
        iframe.remove();
      }
    };
  }, [locale, suppressed, country]);

  return null; // This component doesn't render anything visible
}
